import { useMemo, useState } from 'react'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import { formatMoney, fromMajor, money } from '@/lib/money'
import { cn } from '@/lib/cn'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'
import { BRAND } from '@/brand/brand'

const RAILS = [
  {
    id: 'balance',
    label: `${BRAND.name} balance`,
    detail: 'Instant',
    rate: 0,
    icon: 'account_balance_wallet',
  },
  { id: 'debit', label: 'Debit card', detail: 'Instant', rate: 0.005, icon: 'credit_card' },
  { id: 'credit', label: 'Credit card', detail: 'Instant', rate: 0.025, icon: 'credit_score' },
] as const

export function TopUpScreen() {
  const [amount, setAmount] = useState('200')
  const [rail, setRail] = useState<string>('balance')
  const selected = RAILS.find((item) => item.id === rail) ?? RAILS[0]

  const quote = useMemo(() => {
    const parsed = Number.parseFloat(amount.replace(/,/g, ''))
    const value = fromMajor(Number.isFinite(parsed) && parsed > 0 ? parsed : 0, 'USD')
    const fee = money(Math.round(value.minorUnits * selected.rate), 'USD')
    return { value, fee, total: money(value.minorUnits + fee.minorUnits, 'USD') }
  }, [amount, selected])

  return (
    <>
      <PageHeader
        title="Top up card"
        subtitle={`From your balance it is free. From a card outside ${BRAND.name}, the rail charges and we pass it on.`}
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Stack gap={5}>
              <Stack gap={3}>
                <Text variant="caption" weight="semibold">How much?</Text>
                <div className={styles.amountField}>
                  <Text variant="eyebrow" tone="secondary">To your Mastercard ••4568</Text>
                  <div className={styles.amountRow}>
                    <input
                      className={styles.amountInput}
                      inputMode="decimal"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))}
                      aria-label="Top up amount"
                    />
                    <Text variant="subtitle" weight="semibold" tone="secondary">USD</Text>
                  </div>
                </div>
                <Stack direction="row" gap={2} wrap>
                  {[50, 100, 200, 500].map((preset) => (
                    <Button key={preset} variant="outline" size="sm" onClick={() => setAmount(String(preset))}>
                      ${preset}
                    </Button>
                  ))}
                </Stack>
              </Stack>

              <Stack gap={3}>
                <Text variant="caption" weight="semibold">Where from?</Text>
                <Stack gap={2}>
                  {RAILS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={cn(styles.corridor, item.id === rail && styles.corridorActive)}
                      onClick={() => setRail(item.id)}
                      aria-pressed={item.id === rail}
                    >
                      <Icon name={item.icon} size={20} />
                      <Stack grow gap={0} align="start">
                        <Text variant="caption" weight="semibold">{item.label}</Text>
                        <Text variant="eyebrow" tone="secondary">{item.detail}</Text>
                      </Stack>
                      <Text variant="caption" weight="semibold" numeric>
                        {item.rate === 0 ? 'Free' : `${(item.rate * 100).toFixed(1)}%`}
                      </Text>
                    </button>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">On the card</Text>
              <Pill tone="accent">Instant</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-4">
              {formatMoney(quote.value)}
            </Text>
            <div className={checkout.rule} />
            {[
              ['Top up', formatMoney(quote.value)],
              [`Fee · ${selected.rate === 0 ? 'none' : `${(selected.rate * 100).toFixed(1)}%`}`, formatMoney(quote.fee)],
            ].map(([label, value]) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">{label}</Text>
                <Text variant="caption" weight="semibold" tone="onDark" numeric>{value}</Text>
              </div>
            ))}
            <div className={checkout.rule} />
            <div className={checkout.line}>
              <Text variant="caption" weight="semibold" tone="onDark">Total to pay</Text>
              <Text variant="subtitle" weight="bold" tone="onDark" numeric>{formatMoney(quote.total)}</Text>
            </div>
            <Card.Footer>
              <Button size="lg" block iconEnd="arrow_forward" to="/top-up/done">Top up {formatMoney(quote.total)}</Button>
            </Card.Footer>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
